/**
 * Shared status utilities for institution pages.
 *
 * DashboardInstitutionsPage, CadastroDadosInstitucionaisPage and
 * InstitutionCompliancePrintPage use this module to ensure consistent status
 * labels, colors, badge rendering and counting.
 */

import React from 'react';
import {
  StatusOSC,
  STATUS_OSC_LABELS,
  STATUS_OSC_COLORS,
  calcularStatusAutomatico,
} from '../types/statusOSC.types';
import type { StatusCalculationInput } from '../types/statusOSC.types';
import {
  DOCUMENTOS_OBRIGATORIOS,
  verificarDocumentoProximoVencimento,
  verificarDocumentoVencido,
} from '../types/documentoInstitucional.types';
import type { TipoDocumentoInstitucional } from '../types/documentoInstitucional.types';

export { StatusOSC, STATUS_OSC_LABELS, STATUS_OSC_COLORS, calcularStatusAutomatico };
export type { StatusCalculationInput };

// ---------------------------------------------------------------------------
// Filter options (used in <select> dropdowns on both pages)
// ---------------------------------------------------------------------------

export interface StatusFilterOption {
  value: StatusOSC | 'TODOS';
  label: string;
}

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'TODOS', label: 'Todos os Status' },
  { value: StatusOSC.EM_CADASTRO, label: STATUS_OSC_LABELS[StatusOSC.EM_CADASTRO] },
  { value: StatusOSC.DOCUMENTOS_INCOMPLETOS, label: STATUS_OSC_LABELS[StatusOSC.DOCUMENTOS_INCOMPLETOS] },
  { value: StatusOSC.AGUARDANDO_ANALISE, label: STATUS_OSC_LABELS[StatusOSC.AGUARDANDO_ANALISE] },
  { value: StatusOSC.EM_ANALISE, label: STATUS_OSC_LABELS[StatusOSC.EM_ANALISE] },
  { value: StatusOSC.APROVADO, label: STATUS_OSC_LABELS[StatusOSC.APROVADO] },
  { value: StatusOSC.REPROVADO, label: STATUS_OSC_LABELS[StatusOSC.REPROVADO] },
  { value: StatusOSC.SUSPENSA, label: STATUS_OSC_LABELS[StatusOSC.SUSPENSA] },
  { value: StatusOSC.INATIVA, label: STATUS_OSC_LABELS[StatusOSC.INATIVA] },
];

// ---------------------------------------------------------------------------
// Resolve institution status — handles null/undefined from the backend
// ---------------------------------------------------------------------------

export const resolveStatusOSC = (raw: StatusOSC | null | undefined): StatusOSC =>
  raw ?? StatusOSC.EM_CADASTRO;

// ---------------------------------------------------------------------------
// Quick stats: count institutions by status
// ---------------------------------------------------------------------------

export interface StatusCounts {
  total: number;
  emCadastro: number;
  documentosIncompletos: number;
  aguardandoAnalise: number;
  emAnalise: number;
  aprovado: number;
  reprovado: number;
  suspensa: number;
  inativa: number;
}

export const countByStatus = <T extends { statusOSC?: StatusOSC | null }>(
  institutions: T[]
): StatusCounts => ({
  total: institutions.length,
  emCadastro: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.EM_CADASTRO).length,
  documentosIncompletos: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.DOCUMENTOS_INCOMPLETOS).length,
  aguardandoAnalise: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.AGUARDANDO_ANALISE).length,
  emAnalise: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.EM_ANALISE).length,
  aprovado: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.APROVADO).length,
  reprovado: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.REPROVADO).length,
  suspensa: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.SUSPENSA).length,
  inativa: institutions.filter(i => resolveStatusOSC(i.statusOSC) === StatusOSC.INATIVA).length,
});

// ---------------------------------------------------------------------------
// Document-level status badge (APROVADO / REPROVADO / PENDENTE / EM_ANALISE)
// Used inside CadastroDadosInstitucionaisPage document list rows.
// ---------------------------------------------------------------------------

const DOC_STATUS_COLORS: Record<string, string> = {
  APROVADO: 'bg-green-100 text-green-800',
  REPROVADO: 'bg-red-100 text-red-800',
  PENDENTE: 'bg-yellow-100 text-yellow-800',
  EM_ANALISE: 'bg-blue-100 text-blue-800',
};

const DOC_STATUS_LABELS: Record<string, string> = {
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  PENDENTE: 'Pendente',
  EM_ANALISE: 'Em análise',
};

/**
 * Returns a <span> badge for a document-level status string.
 * Drop-in replacement for the inline renderStatusBadge in CadastroDadosInstitucionaisPage.
 */
export const renderDocStatusBadge = (status: string): React.ReactElement => {
  const cls = DOC_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-800';
  const label = DOC_STATUS_LABELS[status] ?? status;
  return React.createElement(
    'span',
    {
      className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`,
    },
    label
  );
};

// ---------------------------------------------------------------------------
// Status message helper — contextual description shown below the badge
// in CadastroDadosInstitucionaisPage.
// ---------------------------------------------------------------------------

export const getStatusMessage = (
  status: StatusOSC,
  obrigatoriosFaltando: number = 0
): string => {
  switch (status) {
    case StatusOSC.EM_CADASTRO:
      return 'Cadastro iniciado - Aguardando documentos';
    case StatusOSC.DOCUMENTOS_INCOMPLETOS:
      return `${obrigatoriosFaltando} documento(s) obrigatório(s) faltando`;
    case StatusOSC.AGUARDANDO_ANALISE:
      return 'Todos os documentos enviados - Aguardando ser puxado para análise';
    case StatusOSC.EM_ANALISE:
      return 'Em análise pelo analista';
    case StatusOSC.APROVADO:
      return 'Documentos aprovados';
    case StatusOSC.REPROVADO:
      return 'Documentos reprovados - Necessita correções';
    case StatusOSC.SUSPENSA:
      return 'Documentação temporariamente suspensa';
    case StatusOSC.INATIVA:
      return 'Documentação inativa';
    default:
      return '';
  }
};

type DocumentoStatusLike = {
  tipoDocumento: string;
  statusDocumento?: string | null;
  dataAtualizacao?: string;
  dataUpload?: string;
  dataValidade?: string;
};

export interface DocumentStatusDashboard {
  total: number;
  aprovados: number;
  pendentes: number;
  reprovados: number;
  obrigatoriosEnviados: number;
  obrigatoriosTotal: number;
  obrigatoriosFaltando: number;
  todosObrigatoriosAprovados: boolean;
  percentualCompleto: number;
  vencidos: number;
  proximosVencimento: number;
}

export interface InstitutionDocumentStatusSummary<T extends DocumentoStatusLike> {
  status: StatusOSC;
  documentosVisiveis: T[];
  documentosAtuaisPorTipo: Map<string, T>;
  tiposObrigatoriosPendentes: TipoDocumentoInstitucional[];
  dashboard: DocumentStatusDashboard;
}

export const normalizeDocumentoStatus = (status?: string | null): string =>
  (status || '').toString().trim().toLowerCase();

export const isDocumentoReprovado = (status?: string | null): boolean => {
  const normalizedStatus = normalizeDocumentoStatus(status);
  return normalizedStatus === 'reprovado' || normalizedStatus === 'rejeitado' || normalizedStatus === 'vencido';
};

export const isDocumentoAprovado = (status?: string | null): boolean =>
  normalizeDocumentoStatus(status) === 'aprovado';

export const isDocumentoSubstituido = (status?: string | null): boolean =>
  normalizeDocumentoStatus(status) === 'substituido';

export const isDocumentoValidoParaObrigatorio = (status?: string | null): boolean => {
  const normalizedStatus = normalizeDocumentoStatus(status);
  return normalizedStatus.length > 0 &&
    !isDocumentoSubstituido(normalizedStatus) &&
    !isDocumentoReprovado(normalizedStatus);
};

/**
 * O botão de renovação fica sempre disponível, qualquer que seja o status do
 * documento atual (aprovado, reprovado, vencido ou pendente) — decisão de
 * produto para não depender do usuário adivinhar quando pode reenviar.
 * Sempre que já existe um documento para o tipo, o envio precisa ir por
 * substituir (marca o anterior como SUBSTITUIDO), nunca por upload — usar
 * upload nesse caso criaria um segundo documento pendente para o mesmo tipo
 * em vez de renovar o existente.
 */
export const shouldSubstituirDocumento = (
  docAnterior?: { id?: string | null } | null
): boolean => Boolean(docAnterior?.id);

export interface DocumentoVencimentoLike {
  statusDocumento?: string | null;
  dataValidade?: string | null;
}

/**
 * statusDocumento only becomes the literal 'VENCIDO' once the daily
 * DocumentoVencidoMonitorService job runs (08:00). Between the real expiration
 * date and that run, statusDocumento still holds the previous value (e.g.
 * 'aprovado'), so callers must also check dataValidade directly — relying on
 * status alone leaves a document expired-but-unrenewable in the UI until the
 * next run.
 */
export const isDocumentoVencido = (doc: DocumentoVencimentoLike): boolean => {
  if (normalizeDocumentoStatus(doc.statusDocumento) === 'vencido') return true;
  return verificarDocumentoVencido(doc.dataValidade ?? undefined);
};

export const getDocumentoTimestamp = <T extends Pick<DocumentoStatusLike, 'dataAtualizacao' | 'dataUpload'>>(
  doc: T
): number => {
  const rawDate = doc.dataAtualizacao || doc.dataUpload || '';
  const parsedDate = rawDate ? new Date(rawDate).getTime() : 0;
  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

export const getDocumentosAtuaisPorTipo = <T extends DocumentoStatusLike>(docs: T[]): Map<string, T> => {
  const documentosPorTipo = new Map<string, T>();

  docs
    .filter((doc) => !isDocumentoSubstituido(doc.statusDocumento))
    .forEach((doc) => {
      const existingDoc = documentosPorTipo.get(doc.tipoDocumento);
      if (!existingDoc || getDocumentoTimestamp(doc) >= getDocumentoTimestamp(existingDoc)) {
        documentosPorTipo.set(doc.tipoDocumento, doc);
      }
    });

  return documentosPorTipo;
};

export const buildInstitutionDocumentStatusSummary = <T extends DocumentoStatusLike>(
  documentos: T[],
  options?: {
    obrigatorios?: TipoDocumentoInstitucional[];
    statusAtual?: StatusOSC | null;
  }
): InstitutionDocumentStatusSummary<T> => {
  const obrigatoriosSource = options?.obrigatorios?.length
    ? options.obrigatorios
    : DOCUMENTOS_OBRIGATORIOS;
  const documentosVisiveis = documentos.filter((doc) => !isDocumentoSubstituido(doc.statusDocumento));
  const documentosAtuaisPorTipo = getDocumentosAtuaisPorTipo(documentos);
  const tiposObrigatoriosPendentes = obrigatoriosSource.filter((tipo) => {
    const currentDoc = documentosAtuaisPorTipo.get(tipo);
    return !currentDoc || !isDocumentoValidoParaObrigatorio(currentDoc.statusDocumento);
  });
  const validDocs = documentosVisiveis.filter((doc) => isDocumentoValidoParaObrigatorio(doc.statusDocumento));
  const obrigatoriosEnviados = obrigatoriosSource.filter((tipo) => {
    const currentDoc = documentosAtuaisPorTipo.get(tipo);
    return currentDoc ? isDocumentoValidoParaObrigatorio(currentDoc.statusDocumento) : false;
  }).length;
  const todosObrigatoriosAprovados = obrigatoriosSource.length > 0 && obrigatoriosSource.every((tipo) => {
    const currentDoc = documentosAtuaisPorTipo.get(tipo);
    return currentDoc ? isDocumentoAprovado(currentDoc.statusDocumento) : false;
  });
  const docsAtuais = Array.from(documentosAtuaisPorTipo.values()).filter((doc) => !isDocumentoSubstituido(doc.statusDocumento));
  const dashboard: DocumentStatusDashboard = {
    total: documentosVisiveis.length,
    aprovados: documentosVisiveis.filter((doc) => isDocumentoAprovado(doc.statusDocumento)).length,
    pendentes: docsAtuais.filter((doc) =>
      !isDocumentoAprovado(doc.statusDocumento) &&
      !isDocumentoReprovado(doc.statusDocumento)
    ).length,
    reprovados: docsAtuais.filter((doc) => isDocumentoReprovado(doc.statusDocumento)).length,
    obrigatoriosEnviados,
    obrigatoriosTotal: obrigatoriosSource.length,
    obrigatoriosFaltando: tiposObrigatoriosPendentes.length,
    todosObrigatoriosAprovados,
    percentualCompleto: obrigatoriosSource.length > 0
      ? Math.round((obrigatoriosEnviados / obrigatoriosSource.length) * 100)
      : 0,
    vencidos: validDocs.filter((doc) => verificarDocumentoVencido(doc.dataValidade)).length,
    proximosVencimento: validDocs.filter((doc) => verificarDocumentoProximoVencimento(doc.dataValidade) !== null).length,
  };

  return {
    status: calcularStatusAutomatico({
      temDocumentos: documentosVisiveis.length > 0,
      todosObrigatoriosEnviados: dashboard.obrigatoriosFaltando === 0 && dashboard.obrigatoriosTotal > 0,
      todosObrigatoriosAprovados,
      statusAtual: resolveStatusOSC(options?.statusAtual),
    }),
    documentosVisiveis,
    documentosAtuaisPorTipo,
    tiposObrigatoriosPendentes,
    dashboard,
  };
};

